import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Modal,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HijosData from '../data/HijosData';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
};

const ChildRegistrationModal = ({ 
  open, 
  onClose, 
  selectedChild, 
  isEditing, 
  clubes,
  onSave,
  userPadcodigo,
  user
}) => {
  const [childData, setChildData] = useState({
    padcodigo: userPadcodigo || '',
    hicodigo: '',
    nombre: '',
    apellidoPaterno: '',
    apellidoMaterno: '',
    club: '',
    rama: '',
    fnacimiento: '',
    curp: '',
    asociado: false,
    hiinstagram: '@instagram',
    hifacebook: 'facebook.facebook'
  });

  const [curpError, setCurpError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showNotification = (message, severity = 'success') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  const handleCloseNotification = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  useEffect(() => {
    if (selectedChild) {
      setChildData({
        padcodigo: userPadcodigo || selectedChild.padcodigo || '',
        hicodigo: selectedChild.hicodigo || '',
        nombre: selectedChild.nombre || '',
        apellidoPaterno: selectedChild.apellidoPaterno || '',
        apellidoMaterno: selectedChild.apellidoMaterno || '',
        club: selectedChild.club || '',
        rama: selectedChild.rama || '',
        fnacimiento: selectedChild.fnacimiento ? selectedChild.fnacimiento.split('T')[0] : '',
        curp: selectedChild.curp || '',
        asociado: selectedChild.asociado || false,
        hiinstagram: selectedChild.hiinstagram || '@instagram',
        hifacebook: selectedChild.hifacebook || 'facebook.facebook'
      });
    }
  }, [selectedChild, userPadcodigo]);

  const validateAndExtractDateFromCURP = (curp) => {
    if (curp.length !== 18) {
      setCurpError('La CURP debe tener 18 caracteres');
      return null;
    }

    // Extraer componentes de la CURP
    const year = curp.substring(4, 6);
    const month = curp.substring(6, 8);
    const day = curp.substring(8, 10);
    const sex = curp.substring(10, 11);

    // Validar el sexo
    if (sex !== 'H' && sex !== 'M') {
      setCurpError('El sexo en la CURP no es válido');
      return null;
    }

    // Validar la fecha
    const fullYear = parseInt(year) < 50 ? '20' + year : '19' + year;
    const date = new Date(`${fullYear}-${month}-${day}`);
    
    if (isNaN(date.getTime())) {
      setCurpError('La fecha en la CURP no es válida');
      return null;
    }

    // Validar que coincida con el sexo seleccionado
    const selectedSex = childData.rama === 'V' ? 'H' : 'M';
    if (sex !== selectedSex) {
      setCurpError('El sexo en la CURP no coincide con la categoría seleccionada');
      return null;
    }

    setCurpError('');
    return date.toISOString().split('T')[0];
  };

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    setChildData(prev => {
      const newData = {
        ...prev,
        [field]: value
      };

      // Si el campo es CURP y tiene 18 caracteres, validar y actualizar fecha
      if (field === 'curp' && value.length === 18) {
        const birthDate = validateAndExtractDateFromCURP(value);
        if (birthDate) {
          newData.fnacimiento = birthDate;
        }
      }

      return newData;
    });
  };

  const handleSave = async () => {
    if (curpError) {
      return; // No permitir guardar si hay error en la CURP
    }
    try {
      await HijosData.update(childData);
      onSave();
      onClose();
    } catch (error) {
      console.error('Error updating child:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleCreate = async () => {
    // Prevent double submission - immediate disable
    if (isSubmitting || isLoading) {
      return;
    }

    // Disable button immediately
    setIsSubmitting(true);

    if (curpError) {
      showNotification('Por favor corrija el CURP antes de continuar', 'error');
      setIsSubmitting(false);
      return;
    }

    // Validar campos requeridos
    if (!childData.nombre || !childData.apellidoPaterno || !childData.apellidoMaterno || 
        !childData.club || !childData.rama || !childData.fnacimiento || !childData.curp) {
      showNotification('Por favor complete todos los campos requeridos', 'error');
      return;
    }

    // Check if CURP is already registered
    try {
      const existingChildren = await HijosData.fetch(user?.email);
      const isCURPDuplicate = existingChildren.some(child => 
        child.curp === childData.curp && 
        child.hicodigo !== childData.hicodigo // Exclude current child if editing
      );
      
      if (isCURPDuplicate) {
        showNotification('Ya existe un niño registrado con esta CURP', 'error');
        return;
      }
    } catch (error) {
      console.error('Error checking for duplicate CURP:', error);
      showNotification('Error al verificar CURP duplicado', 'error');
      return;
    }

    setIsLoading(true);

    try {
      // Asegurarnos de que padcodigo esté presente
      if (!userPadcodigo) {
        showNotification('Error: No se encontró el código del padre', 'error');
        return;
      }

      // Prepare the data with proper types
      const dataToSend = {
        ...childData,
        padcodigo: parseInt(userPadcodigo),
        club: parseInt(childData.club),
        // Ensure fnacimiento is in the correct format (YYYY-MM-DD)
        fnacimiento: childData.fnacimiento.split('T')[0],
        hiinstagram: childData.hiinstagram || '@instagram',
        hifacebook: childData.hifacebook || 'facebook.facebook'
      };

      // Log the data being sent
      console.log('Sending data to create:', dataToSend);

      const result = await HijosData.create(dataToSend);
      
      console.log('Jugador creado exitosamente:', result);
      showNotification('Jugador creado exitosamente', 'success');
      onSave();
      onClose();
    } catch (error) { 
      console.error('Error creating child:', error);
      showNotification('Error al crear el jugador: ' + error.message, 'error');
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        aria-labelledby="child-modal-title"
      >
        <Box sx={style}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography id="child-modal-title" variant="h6" component="h2">
              {isEditing 
                ? (selectedChild?.nombre === '' ? 'Agregar Jugador' : 'Editar Jugador')
                : 'Información del Jugador'}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          
          {selectedChild && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Nombre"
                value={childData.nombre}
                onChange={handleInputChange('nombre')}
                disabled={!isEditing}
                fullWidth
              />
              <TextField
                label="Apellido Paterno"
                value={childData.apellidoPaterno}
                onChange={handleInputChange('apellidoPaterno')}
                disabled={!isEditing}
                fullWidth
              />
              <TextField
                label="Apellido Materno"
                value={childData.apellidoMaterno}
                onChange={handleInputChange('apellidoMaterno')}
                disabled={!isEditing}
                fullWidth
              />
              <FormControl fullWidth disabled={!isEditing}>
                <InputLabel>Club</InputLabel>
                <Select
                  value={childData.club}
                  onChange={handleInputChange('club')}
                  label="Club"
                >
                  {clubes
                    .filter((club) => Number(club.clubasociado) === 1)
                    .map((club) => (
                    <MenuItem key={club.id} value={club.id}>
                      {club.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth disabled={!isEditing}>
                <InputLabel>Categoría</InputLabel>
                <Select
                  value={childData.rama}
                  onChange={handleInputChange('rama')}
                  label="Categoría"
                >
                  <MenuItem value="V">Varonil</MenuItem>
                  <MenuItem value="F">Femenil</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Fecha de Nacimiento"
                type="date"
                value={childData.fnacimiento}
                disabled={true}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="CURP"
                value={childData.curp}
                onChange={handleInputChange('curp')}
                disabled={!isEditing}
                fullWidth
                error={!!curpError}
                helperText={curpError}
                inputProps={{ maxLength: 18 }}
              />
                             {isEditing && (
                                   <Button
                    variant="contained"
                    onClick={selectedChild?.nombre === '' ? handleCreate : handleSave}
                    sx={{ mt: 2 }}
                    disabled={!!curpError || isLoading || isSubmitting}
                    startIcon={isLoading ? <CircularProgress size={20} /> : undefined}
                  >
                   {selectedChild?.nombre === '' 
                     ? (isLoading ? 'Guardando...' : 'Agregar Jugador') 
                     : (isLoading ? 'Guardando...' : 'Guardar Cambios')
                   }
                 </Button>
               )}
            </Box>
          )}
        </Box>
      </Modal>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChildRegistrationModal; 